import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import type {IconProp} from '@fortawesome/fontawesome-svg-core';
import {
  faDownload,
  faNoteSticky,
  faPen,
  faPlus,
  faSpinner,
  faTimes,
  faTrash,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import ExcelJS from 'exceljs';

import {
  apiDeleteFdOperatingCost,
  apiGetFdOperatingExpenses,
  apiGetFdProductionPlansByApplicationId,
  apiGetFdProjectedRevenuesByApplicationId,
  apiUpdateFdProductionPlans,
  apiUpsertFdOperatingCost,
  apiUpsertFdProductionCost,
  apiGetFdFinancialRatios,
  apiUpsertFdProjectedRevenue,
  type IFdProjectedRevenueLookup,
} from 'constants/Api';
import type {
  IFdApplicationType,
  IFdOperatingCost,
  IFdProductionCost,
  IFdProductionPlan,
  IGenericType,
} from 'constants/interfaces';
import {
  lshParseOperatingCostMonthlyCosts,
  lshSerializeOperatingCostMonthlyCosts,
} from './steps/financials/financials.utils';

interface RevenueRow {
  planId: number;
  cropId: string;
  cropName: string;
  plantingDate: string;
  irrigation: string;
  plantPopulation: number;
  areaOfDryLand: number;
  areaUnderIrrigation: number;
  notes: string;
  numberOfHectares: number;
  estimatedYield: number;
  estimatedPrice: number;
  startMonth: string;
  endMonth: string;
  harvestYear?: number | null;
  harvestMonth?: number | null;
  projectedRevenueId?: string;
}

interface ProductionRow {
  planId: number;
  cropId: string;
  cropName: string;
  plantingDate: string;
  hectares: string;
  seedCost: string;
  fertiliserCost: string;
  chemicalCost: string;
  notes: string;
  year?: number;
  productionCostId?: number;
}

type OperatingExpenseFrequency =
  | 'once-off'
  | 'monthly'
  | 'quarterly'
  | 'custom';

type MonthlyValueMap = Record<string, number>;

interface OperatingRow {
  localId: number;
  backendId?: number;
  expenseId: string;
  categoryName: string;
  notes: string;
  frequency: OperatingExpenseFrequency;
  monthlyValues: MonthlyValueMap;
}

interface MonthColumn {
  key: string;
  label: string;
}

type NoteTargetSection = 'revenue' | 'production' | 'operating';

interface NoteTarget {
  section: NoteTargetSection;
  id: number;
  label: string;
}

interface OperatingCostConfig {
  frequency?: string;
  total?: number | string;
  month?: string;
  months?: string[];
  quarters?: string[];
  selectedMonth?: string;
  selectedMonths?: string[];
  selectedQuarters?: string[];
  monthlyValues?: Record<string, unknown>;
}

export interface ApplicationFinancialsCashflowController {
  canSubmit: boolean;
  isLoading: boolean;
  isSaving: boolean;
  save: () => Promise<void>;
}

interface ApplicationFinancialsCashflowStepProps {
  applicationId: number;
  application?: IFdApplicationType;
  onRegisterController: (
    controller: ApplicationFinancialsCashflowController,
  ) => void;
  onComplete: () => Promise<void> | void;
}

const PERIOD_MONTHS = 18;
const DEFAULT_START_MONTH_INDEX = 7;
const TEMPLATE_SHEET_REVENUE = 'Projected revenue';
const TEMPLATE_SHEET_PRODUCTION = 'Production costs';
const TEMPLATE_SHEET_OPERATING = 'Operating costs';
const TEMPLATE_SHEET_SETTINGS = 'Settings';
const FOCUSABLE_ELEMENT_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const MONTH_LABELS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const MONTH_KEY_PATTERN =
  /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)(\d{2})$/i;

const iconDownload = faDownload as IconProp;
const iconNoteSticky = faNoteSticky as IconProp;
const iconPen = faPen as IconProp;
const iconPlus = faPlus as IconProp;
const iconSpinner = faSpinner as IconProp;
const iconTimes = faTimes as IconProp;
const iconTrash = faTrash as IconProp;
const iconUpload = faUpload as IconProp;

function useModalFocusTrap(
  isOpen: boolean,
  panelRef: React.RefObject<HTMLDivElement>,
  onRequestClose: () => void,
) {
  useEffect(() => {
    const node = panelRef.current;
    if (!isOpen || !node) return;

    const getFocusableElements = () =>
      Array.from(
        node.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENT_SELECTOR),
      ).filter(
        (element) =>
          !element.hasAttribute('disabled') &&
          element.getAttribute('aria-hidden') !== 'true',
      );

    const focusInitial = () => {
      const focusable = getFocusableElements();
      if (focusable.length > 0) {
        window.requestAnimationFrame(() => focusable[0].focus());
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onRequestClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    focusInitial();
    node.addEventListener('keydown', handleKeyDown);

    return () => {
      node.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onRequestClose, panelRef]);
}

function uniqueLocalId() {
  return Number(`${Date.now()}${Math.floor(Math.random() * 1000)}`);
}

function addMonths(date: Date, months: number) {
  const copy = new Date(date);
  copy.setMonth(copy.getMonth() + months);
  return copy;
}

function deriveDefaultWindow(plantingDate?: string | null) {
  const base = plantingDate ? new Date(plantingDate) : new Date();
  const start = addMonths(base, 6);
  const end = addMonths(start, 3);

  return {
    startMonth: MONTH_NAMES[start.getMonth()] ?? MONTH_NAMES[0],
    endMonth: MONTH_NAMES[end.getMonth()] ?? MONTH_NAMES[0],
  };
}

function roundMoney(value: number) {
  return Number((Number.isFinite(value) ? value : 0).toFixed(2));
}

function parseAmount(value: string | number | null | undefined) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  const raw = String(value ?? '').trim();
  if (!raw) return 0;

  const cleaned = raw.replace(/[^\d.,-]/g, '').replace(/,/g, '.');
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

function sanitizeAmountInput(value: string) {
  const cleaned = value
    .replace(/[^\d.,-]/g, '')
    .replace(/,/g, '.')
    .replace(/^(-)?\./, '$10.');
  const parts = cleaned.split('.');
  return parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : cleaned;
}

function toCurrency(value: number) {
  return `R${new Intl.NumberFormat('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(roundMoney(value))}`;
}

function formatInput(value: number) {
  return value === 0 ? '' : roundMoney(value).toFixed(2);
}

function normaliseHeader(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function normaliseMonthKey(value: unknown) {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

function parseMonthKey(value: unknown) {
  const match = normaliseMonthKey(value).match(MONTH_KEY_PATTERN);
  if (!match) return null;

  const monthKey = match[1].toLowerCase();
  const monthIndex = MONTH_LABELS.indexOf(monthKey);
  if (monthIndex < 0) return null;

  const year = 2000 + Number(match[2]);
  return {
    monthIndex,
    year,
    sortValue: year * 12 + monthIndex,
  };
}

function isMonthKey(value: unknown) {
  return Boolean(parseMonthKey(value));
}

function monthLabelFromId(id: string) {
  const parsed = parseMonthKey(id);
  if (!parsed) return id;

  return `${MONTH_LABELS[parsed.monthIndex].toUpperCase()}${String(
    parsed.year,
  ).slice(-2)}`;
}

function monthKeyFromTemplateHeader(value: string) {
  const compact = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  if (!compact) return null;

  if (isMonthKey(compact)) {
    return compact;
  }

  const fullYearMatch = compact.match(
    /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)(20\d{2})$/,
  );
  if (fullYearMatch) {
    return `${fullYearMatch[1]}${fullYearMatch[2].slice(-2)}`;
  }

  return null;
}

function parseTemplateNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const normalized = raw.replace(/\s+/g, '').replace(/,/g, '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function resolveMonthName(value: unknown): string | null {
  const raw = String(value ?? '').trim();
  if (!raw) return null;

  const compact = raw.toLowerCase().replace(/[^a-z]/g, '');
  if (!compact) return null;

  const match = MONTH_NAMES.find((month) => {
    const normalisedMonth = month.toLowerCase();
    return normalisedMonth === compact || normalisedMonth.startsWith(compact);
  });

  return match ?? null;
}

function parseTemplateAmount(value: unknown) {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? roundMoney(value) : null;
  }

  const raw = String(value).trim();
  if (!raw) return 0;

  const withoutCurrency = raw.replace(/\s+/g, '').replace(/^R/i, '');
  if (!withoutCurrency) return 0;

  const decimalCommaPattern = /^-?\d+(,\d+)?$/;
  const normalised = decimalCommaPattern.test(withoutCurrency)
    ? withoutCurrency.replace(',', '.')
    : withoutCurrency.replace(/,/g, '');
  const parsed = Number(normalised);
  return Number.isFinite(parsed) ? roundMoney(parsed) : null;
}

function getMonthIndex(monthName: string) {
  const index = MONTH_NAMES.findIndex((month) => month === monthName);
  return index >= 0 ? index : 0;
}

function getWindowMonthIndexes(startMonth: string, endMonth: string) {
  const start = getMonthIndex(startMonth);
  const end = getMonthIndex(endMonth);
  const indexes: number[] = [];

  let cursor = start;
  indexes.push(cursor);
  while (cursor !== end) {
    cursor = (cursor + 1) % MONTH_NAMES.length;
    indexes.push(cursor);
    if (indexes.length > MONTH_NAMES.length) break;
  }

  return indexes;
}

function generateMonthlyPeriod(startYear: number, startMonthIndex: number) {
  const months: string[] = [];

  for (let index = 0; index < PERIOD_MONTHS; index += 1) {
    const year = startYear + Math.floor((startMonthIndex + index) / 12);
    const monthIndex = (startMonthIndex + index) % 12;
    months.push(`${MONTH_LABELS[monthIndex]}${String(year).slice(-2)}`);
  }

  return months;
}

function normalisePeriodYear(value: number) {
  if (!Number.isFinite(value)) return new Date().getFullYear();

  const integer = Math.trunc(value);
  if (integer < 2000) return 2000;
  if (integer > 2100) return 2100;
  return integer;
}

function inferPeriodStartFromMonthKeys(monthKeys: string[]) {
  const parsed = monthKeys
    .map((key) => ({key, parsed: parseMonthKey(key)}))
    .filter(
      (
        entry,
      ): entry is {
        key: string;
        parsed: NonNullable<ReturnType<typeof parseMonthKey>>;
      } => Boolean(entry.parsed),
    )
    .sort((left, right) => left.parsed.sortValue - right.parsed.sortValue);

  if (parsed.length === 0) return null;

  return {
    year: parsed[0].parsed.year,
    monthIndex: parsed[0].parsed.monthIndex,
  };
}

function distributeAcrossMonths(
  total: number,
  months: string[],
  into: MonthlyValueMap,
) {
  if (months.length === 0) return;

  const perMonth = roundMoney(total / months.length);
  let allocated = 0;

  months.forEach((month, index) => {
    if (index === months.length - 1) {
      into[month] = roundMoney(total - allocated);
      return;
    }

    into[month] = perMonth;
    allocated += perMonth;
  });
}

function quarterMonthsFromKeys(quarterId: unknown, monthKeys: string[]) {
  const quarterMatch = String(quarterId ?? '').match(/^Q([1-4])$/i);
  if (!quarterMatch) return [];

  const quarterNumber = Number(quarterMatch[1]);
  const startMonthIndex = (quarterNumber - 1) * 3;
  const monthIndexes = new Set([
    startMonthIndex,
    startMonthIndex + 1,
    startMonthIndex + 2,
  ]);

  return monthKeys.filter((monthKey) => {
    const parsed = parseMonthKey(monthKey);
    if (!parsed) return false;
    return monthIndexes.has(parsed.monthIndex);
  });
}

function parseOperatingCostConfig(
  record: IFdOperatingCost,
): OperatingCostConfig {
  try {
    const parsed = JSON.parse(record.monthlyCosts || '{}');
    const base =
      parsed && typeof parsed === 'object'
        ? (parsed as OperatingCostConfig)
        : {};

    const normalised = lshParseOperatingCostMonthlyCosts(record.monthlyCosts);
    if (Object.keys(normalised.monthlyValues).length > 0) {
      return {
        ...base,
        monthlyValues: normalised.monthlyValues,
        total: normalised.total,
        frequency: normalised.frequency || base.frequency,
      };
    }

    return base;
  } catch (error) {
    console.error('Failed to parse operating cost monthlyCosts', error);
    return {};
  }
}

function collectMonthKeysFromOperatingCosts(
  costs: IFdOperatingCost[],
  fallback: string[],
) {
  const set = new Set<string>();
  const addMonthKey = (value: unknown) => {
    const key = normaliseMonthKey(value);
    if (isMonthKey(key)) set.add(key);
  };

  fallback.forEach(addMonthKey);

  costs.forEach((cost) => {
    const config = parseOperatingCostConfig(cost);

    addMonthKey(config.selectedMonth);

    addMonthKey(config.month);

    [...(config.selectedMonths ?? []), ...(config.months ?? [])].forEach(
      addMonthKey,
    );

    Object.keys(config.monthlyValues ?? {}).forEach(addMonthKey);
  });

  return Array.from(set).sort((left, right) => {
    const parsedLeft = parseMonthKey(left);
    const parsedRight = parseMonthKey(right);
    if (!parsedLeft || !parsedRight) return left.localeCompare(right);
    return parsedLeft.sortValue - parsedRight.sortValue;
  });
}

function hydrateMonthlyValues(record: IFdOperatingCost, monthKeys: string[]) {
  const hydrated: MonthlyValueMap = {};
  const config = parseOperatingCostConfig(record);

  monthKeys.forEach((month) => {
    hydrated[month] = 0;
  });

  Object.entries(config.monthlyValues ?? {}).forEach(([key, rawValue]) => {
    const normalisedKey = normaliseMonthKey(key);
    if (!(normalisedKey in hydrated)) return;
    hydrated[normalisedKey] = roundMoney(Number(rawValue) || 0);
  });

  const hasExplicitValues = Object.values(hydrated).some((value) => value > 0);
  if (hasExplicitValues) {
    return hydrated;
  }

  const total = parseAmount(config.total ?? 0);
  if (total <= 0 || monthKeys.length === 0) {
    return hydrated;
  }

  const selectedMonth = normaliseMonthKey(config.selectedMonth || config.month);
  if (selectedMonth && selectedMonth in hydrated) {
    hydrated[selectedMonth] = roundMoney(total);
    return hydrated;
  }

  const selectedMonths = [
    ...(config.selectedMonths ?? []),
    ...(config.months ?? []),
  ]
    .map(normaliseMonthKey)
    .filter((month) => month in hydrated);
  if (selectedMonths.length > 0) {
    distributeAcrossMonths(total, selectedMonths, hydrated);
    return hydrated;
  }

  const quarterMonths = (config.selectedQuarters ?? config.quarters ?? [])
    .flatMap((quarterId) => quarterMonthsFromKeys(quarterId, monthKeys))
    .filter((month, index, list) => list.indexOf(month) === index);
  if (quarterMonths.length > 0) {
    distributeAcrossMonths(total, quarterMonths, hydrated);
    return hydrated;
  }

  if (config.frequency === 'monthly') {
    distributeAcrossMonths(total, monthKeys, hydrated);
    return hydrated;
  }

  hydrated[monthKeys[0]] = roundMoney(total);
  return hydrated;
}

function totalForOperatingRow(row: OperatingRow, monthKeys: string[]) {
  return monthKeys.reduce(
    (sum, key) => sum + roundMoney(Number(row.monthlyValues[key] ?? 0)),
    0,
  );
}

function buildOperatingPayload(row: OperatingRow, monthKeys: string[]) {
  const monthlyValues: MonthlyValueMap = {};
  monthKeys.forEach((key) => {
    monthlyValues[key] = roundMoney(Number(row.monthlyValues[key] ?? 0));
  });

  const nonZeroMonths = monthKeys.filter((key) => monthlyValues[key] > 0);
  let frequency: OperatingExpenseFrequency = row.frequency || 'custom';

  if (nonZeroMonths.length <= 1) {
    frequency = 'once-off';
  } else if (
    nonZeroMonths.length === monthKeys.length &&
    monthKeys.length > 1
  ) {
    frequency = 'monthly';
  }

  const total = roundMoney(totalForOperatingRow(row, monthKeys));

  return {
    monthlyValues,
    frequency,
    selectedMonth: nonZeroMonths.length === 1 ? nonZeroMonths[0] : undefined,
    selectedMonths: nonZeroMonths,
    selectedQuarters: [] as string[],
    total,
  };
}

function resolveRevenueMonthsForPeriod(
  startMonth: string,
  endMonth: string,
  monthKeys: string[],
) {
  const sequence = getWindowMonthIndexes(startMonth, endMonth);
  if (sequence.length === 0) {
    return monthKeys.slice(0, Math.min(4, monthKeys.length));
  }

  const result: string[] = [];
  let cursor = 0;

  sequence.forEach((monthIndex) => {
    const found = monthKeys.findIndex((monthKey, index) => {
      if (index < cursor) return false;
      const parsed = parseMonthKey(monthKey);
      return parsed?.monthIndex === monthIndex;
    });

    if (found !== -1) {
      result.push(monthKeys[found]);
      cursor = found + 1;
    }
  });

  return result.length > 0
    ? result
    : monthKeys.slice(0, Math.min(4, monthKeys.length));
}

function revenueTotal(row: RevenueRow) {
  return roundMoney(
    row.numberOfHectares * row.estimatedYield * row.estimatedPrice,
  );
}

function productionTotal(row: ProductionRow) {
  return roundMoney(
    parseAmount(row.seedCost) +
      parseAmount(row.fertiliserCost) +
      parseAmount(row.chemicalCost),
  );
}

function revenueMonthlyValues(row: RevenueRow, monthKeys: string[]) {
  const total = revenueTotal(row);
  const windowMonths = resolveRevenueMonthsForPeriod(
    row.startMonth,
    row.endMonth,
    monthKeys,
  );
  const monthly: MonthlyValueMap = {};

  monthKeys.forEach((key) => {
    monthly[key] = 0;
  });

  distributeAcrossMonths(total, windowMonths, monthly);
  return monthly;
}

function productionMonthlyValues(row: ProductionRow, monthKeys: string[]) {
  const total = productionTotal(row);
  const plantingDate = new Date(row.plantingDate);
  const monthIndex = Number.isNaN(plantingDate.getTime())
    ? null
    : plantingDate.getMonth();

  const startIndex =
    monthIndex === null
      ? 0
      : Math.max(
          0,
          monthKeys.findIndex(
            (monthKey) => parseMonthKey(monthKey)?.monthIndex === monthIndex,
          ),
        );

  const selectedMonths = monthKeys.slice(startIndex, startIndex + 3);
  const monthly: MonthlyValueMap = {};

  monthKeys.forEach((key) => {
    monthly[key] = 0;
  });

  distributeAcrossMonths(total, selectedMonths, monthly);
  return monthly;
}

function resolveCellValue(value: ExcelJS.CellValue): unknown {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') return value;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') {
    if ('result' in value && value.result !== undefined) {
      return resolveCellValue(value.result as ExcelJS.CellValue);
    }
    if ('text' in value && typeof value.text === 'string') {
      return value.text;
    }
    if (
      'richText' in value &&
      Array.isArray(value.richText) &&
      value.richText.length > 0
    ) {
      return value.richText.map((item) => item.text).join('');
    }
  }
  return String(value);
}

function worksheetToMatrix(worksheet: ExcelJS.Worksheet) {
  const rows: unknown[][] = [];

  worksheet.eachRow({includeEmpty: false}, (row) => {
    const values: unknown[] = [];
    row.eachCell({includeEmpty: true}, (cell, columnNumber) => {
      values[columnNumber - 1] = resolveCellValue(cell.value);
    });
    rows.push(values);
  });

  return rows;
}

const ApplicationFinancialsCashflowStep: React.FC<
  ApplicationFinancialsCashflowStepProps
> = ({applicationId, application, onRegisterController, onComplete}) => {
  const defaultStartYear = new Date().getFullYear();
  const defaultMonthlyPeriod = useMemo(
    () => generateMonthlyPeriod(defaultStartYear, DEFAULT_START_MONTH_INDEX),
    [defaultStartYear],
  );

  const [revenueRows, setRevenueRows] = useState<RevenueRow[]>([]);
  const [productionRows, setProductionRows] = useState<ProductionRow[]>([]);
  const [operatingRows, setOperatingRows] = useState<OperatingRow[]>([]);
  const [financialRatios, setFinancialRatios] = useState<
    import('constants/interfaces').IFdFinancialRatios | null
  >(null);
  const [catalogue, setCatalogue] = useState<IGenericType[]>([]);
  const [periodStartYear, setPeriodStartYear] =
    useState<number>(defaultStartYear);
  const [periodStartMonthIndex, setPeriodStartMonthIndex] = useState<number>(
    DEFAULT_START_MONTH_INDEX,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [isRevenueModalOpen, setIsRevenueModalOpen] = useState(false);
  const [editingRevenueRow, setEditingRevenueRow] = useState<RevenueRow | null>(
    null,
  );
  const [revenueFormError, setRevenueFormError] = useState('');
  const [isProductionModalOpen, setIsProductionModalOpen] = useState(false);
  const [editingProductionRow, setEditingProductionRow] =
    useState<ProductionRow | null>(null);
  const [productionFormError, setProductionFormError] = useState('');
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteTarget, setNoteTarget] = useState<NoteTarget | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const revenueModalPanelRef = useRef<HTMLDivElement | null>(null);
  const revenueModalTriggerRef = useRef<HTMLButtonElement | null>(null);
  const productionModalPanelRef = useRef<HTMLDivElement | null>(null);
  const productionModalTriggerRef = useRef<HTMLButtonElement | null>(null);
  const noteModalPanelRef = useRef<HTMLDivElement | null>(null);
  const noteModalTriggerRef = useRef<HTMLButtonElement | null>(null);

  const monthKeys = useMemo(
    () => generateMonthlyPeriod(periodStartYear, periodStartMonthIndex),
    [periodStartMonthIndex, periodStartYear],
  );

  const monthColumns = useMemo<MonthColumn[]>(
    () => monthKeys.map((key) => ({key, label: monthLabelFromId(key)})),
    [monthKeys],
  );

  const periodYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const minYear = Math.min(2020, periodStartYear - 5, currentYear - 5);
    const maxYear = Math.max(2030, periodStartYear + 5, currentYear + 8);

    return Array.from(
      {length: maxYear - minYear + 1},
      (_, index) => minYear + index,
    );
  }, [periodStartYear]);

  const periodLabel = `${MONTH_NAMES[periodStartMonthIndex]} ${periodStartYear}`;

  const setStatusOnce = useCallback((message: string) => {
    setStatusMessage(message);
    if (!message) return;

    window.setTimeout(() => setStatusMessage(''), 4000);
  }, []);

  const loadData = useCallback(async () => {
    if (!Number.isFinite(applicationId)) return;

    try {
      setIsLoading(true);

      const [plans, projectedRevenueRecords, expenseCatalogue, ratios] =
        await Promise.all([
          apiGetFdProductionPlansByApplicationId(applicationId),
          apiGetFdProjectedRevenuesByApplicationId(applicationId),
          apiGetFdOperatingExpenses(),
          apiGetFdFinancialRatios(applicationId).catch(() => null),
        ]);

      setFinancialRatios(ratios);

      const projectedByCrop = new Map<string, IFdProjectedRevenueLookup>();
      projectedRevenueRecords.forEach((record) => {
        if (record.cropId) {
          projectedByCrop.set(record.cropId, record);
        }
      });

      const productionCosts = application?.productionCosts || [];
      const operatingCosts = application?.operatingCosts || [];
      const productionCostByCrop = new Map<string, IFdProductionCost>();
      productionCosts.forEach((record) => {
        if (record.crop?.id) {
          productionCostByCrop.set(String(record.crop.id), record);
        }
      });

      const mappedRevenueRows = (plans ?? []).reduce<RevenueRow[]>(
        (accumulator, plan: IFdProductionPlan) => {
          const cropId = plan.crop?.id ? String(plan.crop.id) : '';
          if (!cropId) return accumulator;

          const projected = projectedByCrop.get(cropId);
          const defaults = deriveDefaultWindow(plan.plantingDate);

          accumulator.push({
            planId: Number(plan.id),
            cropId,
            cropName: plan.crop?.name ?? 'Unknown crop',
            plantingDate: plan.plantingDate ?? new Date().toISOString(),
            irrigation: String(plan.irrigation ?? ''),
            plantPopulation: Number(plan.plantPopulation ?? 0) || 0,
            areaOfDryLand: Number(plan.areaOfDryLand ?? 0) || 0,
            areaUnderIrrigation: Number(plan.areaUnderIrrigation ?? 0) || 0,
            notes: plan.notes ?? '',
            numberOfHectares: Number(plan.numberOfHectares ?? 0),
            estimatedYield: Number(plan.estimatedYield ?? 0),
            estimatedPrice: Number(plan.estimatedPrice ?? 0),
            startMonth: projected?.window.startMonth || defaults.startMonth,
            endMonth: projected?.window.endMonth || defaults.endMonth,
            harvestYear: plan.harvestYear ?? null,
            harvestMonth: plan.harvestMonth ?? null,
            projectedRevenueId: projected?.id,
          });
          return accumulator;
        },
        [],
      );

      const mappedProductionRows = (plans ?? []).reduce<ProductionRow[]>(
        (accumulator, plan: IFdProductionPlan) => {
          const cropId = plan.crop?.id ? String(plan.crop.id) : '';
          if (!cropId) return accumulator;

          const linked = productionCostByCrop.get(cropId);

          accumulator.push({
            planId: Number(plan.id),
            cropId,
            cropName: plan.crop?.name ?? 'Unknown crop',
            plantingDate: plan.plantingDate ?? new Date().toISOString(),
            hectares: formatInput(Number(linked?.numberOfHectares ?? 0)),
            seedCost: formatInput(Number(linked?.seed ?? 0)),
            fertiliserCost: formatInput(Number(linked?.fertiliser ?? 0)),
            chemicalCost: formatInput(Number(linked?.chemical ?? 0)),
            notes: linked?.notes ?? '',
            year: linked?.year,
            productionCostId: linked?.id ? Number(linked.id) : undefined,
          });
          return accumulator;
        },
        [],
      );

      const resolvedExpenseMonthKeys = collectMonthKeysFromOperatingCosts(
        operatingCosts,
        defaultMonthlyPeriod,
      );
      const inferredPeriodStart = inferPeriodStartFromMonthKeys(
        resolvedExpenseMonthKeys,
      );
      const effectiveMonthKeys = inferredPeriodStart
        ? generateMonthlyPeriod(
            inferredPeriodStart.year,
            inferredPeriodStart.monthIndex,
          )
        : resolvedExpenseMonthKeys;

      if (inferredPeriodStart) {
        setPeriodStartYear(normalisePeriodYear(inferredPeriodStart.year));
        setPeriodStartMonthIndex(inferredPeriodStart.monthIndex);
      }

      const mappedOperatingRows: OperatingRow[] = operatingCosts.map((cost) => {
        const config = parseOperatingCostConfig(cost);
        return {
          localId: Number(cost.id) || uniqueLocalId(),
          backendId: cost.id ? Number(cost.id) : undefined,
          expenseId: String(cost.expense?.id ?? ''),
          categoryName: cost.expense?.name ?? 'Operating expense',
          notes: cost.notes ?? '',
          frequency:
            (config.frequency as OperatingExpenseFrequency) || 'custom',
          monthlyValues: hydrateMonthlyValues(cost, effectiveMonthKeys),
        };
      });

      setRevenueRows(mappedRevenueRows);
      setProductionRows(mappedProductionRows);
      setOperatingRows(mappedOperatingRows);
      setCatalogue(expenseCatalogue);

      if (
        mappedRevenueRows.length === 0 &&
        mappedProductionRows.length === 0 &&
        mappedOperatingRows.length === 0
      ) {
        setStatusMessage('No cashflow data found yet.');
      } else {
        setStatusMessage('');
      }
    } catch (error) {
      console.error('Failed to load cashflow data', error);
      setStatusMessage('Failed to load cashflow data.');
      window.alert(
        'We could not load cashflow data right now. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  }, [application, applicationId, defaultMonthlyPeriod]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const canSubmit = useMemo(
    () =>
      Number.isFinite(applicationId) &&
      revenueRows.length > 0 &&
      productionRows.length > 0,
    [applicationId, productionRows.length, revenueRows.length],
  );

  const handleSaveAll = useCallback(async () => {
    if (isSaving) return;

    if (!Number.isFinite(applicationId)) {
      window.alert(
        'We could not find the application details. Please refresh and try again.',
      );
      return;
    }

    if (revenueRows.length === 0 || productionRows.length === 0) {
      window.alert('Production plan rows are required to save cashflow.');
      return;
    }

    if (
      operatingRows.some(
        (row) => !row.expenseId || Number.isNaN(Number(row.expenseId)),
      )
    ) {
      window.alert(
        'Each operating cost row must have a valid expense category.',
      );
      return;
    }

    setIsSaving(true);

    try {
      const reportingYear =
        parseMonthKey(monthKeys[0])?.year ?? new Date().getFullYear();
      const productionPlansData = revenueRows.map((row) => ({
        id: row.planId,
        cropId: Number(row.cropId),
        plantingDate: row.plantingDate,
        irrigation: row.irrigation as any,
        plantPopulation: Number(row.plantPopulation ?? 0) || 0,
        estimatedPrice: Number(row.estimatedPrice ?? 0) || 0,
        estimatedYield: Number(row.estimatedYield ?? 0) || 0,
        areaOfDryLand: Number(row.areaOfDryLand ?? 0) || 0,
        areaUnderIrrigation: Number(row.areaUnderIrrigation ?? 0) || 0,
        applicationId: Number(applicationId),
        numberOfHectares: Number(row.numberOfHectares ?? 0) || 0,
        notes: row.notes ?? '',
        harvestYear: row.harvestYear ?? null,
        harvestMonth: row.harvestMonth ?? null,
      }));

      await apiUpdateFdProductionPlans(productionPlansData);

      const updatedRevenueRows: RevenueRow[] = [];
      for (const row of revenueRows) {
        const response = await apiUpsertFdProjectedRevenue({
          id: row.projectedRevenueId,
          monthlyCosts: {
            startMonth: row.startMonth,
            endMonth: row.endMonth,
          },
          year: reportingYear,
          cropId: Number(row.cropId),
          applicationId: Number(applicationId),
        });

        updatedRevenueRows.push({
          ...row,
          projectedRevenueId: response?.id ?? row.projectedRevenueId,
        });
      }

      const updatedProductionRows: ProductionRow[] = [];
      for (const row of productionRows) {
        const response = await apiUpsertFdProductionCost({
          id: row.productionCostId,
          year: row.year ?? reportingYear,
          plantingDate: row.plantingDate,
          numberOfHectares: parseAmount(row.hectares),
          seed: parseAmount(row.seedCost),
          fertiliser: parseAmount(row.fertiliserCost),
          chemical: parseAmount(row.chemicalCost),
          notes: row.notes,
          cropId: Number(row.cropId),
          applicationId: Number(applicationId),
        });

        updatedProductionRows.push({
          ...row,
          year: response?.year ?? row.year ?? reportingYear,
          productionCostId: response?.id
            ? Number(response.id)
            : row.productionCostId,
        });
      }

      const updatedOperatingRows: OperatingRow[] = [];
      for (const row of operatingRows) {
        const payload = buildOperatingPayload(row, monthKeys);
        const monthlyCosts = lshSerializeOperatingCostMonthlyCosts(
          payload.monthlyValues,
          payload.frequency,
        );

        const response = await apiUpsertFdOperatingCost({
          id: row.backendId,
          expenseId: Number(row.expenseId),
          applicationId: Number(applicationId),
          notes: row.notes,
          year: reportingYear,
          monthlyCosts,
        });

        const expenseName =
          catalogue.find((item) => String(item.id) === String(row.expenseId))
            ?.name ?? row.categoryName;

        updatedOperatingRows.push({
          ...row,
          backendId: response?.id ? Number(response.id) : row.backendId,
          categoryName: expenseName,
          monthlyValues: payload.monthlyValues,
          frequency: payload.frequency,
        });
      }

      setRevenueRows(updatedRevenueRows);
      setProductionRows(updatedProductionRows);
      setOperatingRows(updatedOperatingRows);
      setStatusOnce('Cashflow saved successfully.');

      await onComplete();
    } catch (error) {
      console.error('Failed to save cashflow', error);
      setStatusOnce('Failed to save cashflow.');
      window.alert('We could not save cashflow right now. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [
    applicationId,
    catalogue,
    isSaving,
    monthKeys,
    onComplete,
    operatingRows,
    productionRows,
    revenueRows,
    setStatusOnce,
  ]);

  useEffect(() => {
    onRegisterController({
      canSubmit,
      isLoading,
      isSaving,
      save: handleSaveAll,
    });
  }, [canSubmit, handleSaveAll, isLoading, isSaving, onRegisterController]);

  const openRevenueModal = useCallback(
    (row: RevenueRow, trigger: HTMLButtonElement | null) => {
      setEditingRevenueRow({...row});
      setRevenueFormError('');
      if (trigger) {
        revenueModalTriggerRef.current = trigger;
      }
      setIsRevenueModalOpen(true);
    },
    [],
  );

  const closeRevenueModal = useCallback(() => {
    setIsRevenueModalOpen(false);
    setEditingRevenueRow(null);
    setRevenueFormError('');
  }, []);

  const handleRevenueModalFieldChange = useCallback(
    (
      field:
        | 'startMonth'
        | 'endMonth'
        | 'numberOfHectares'
        | 'estimatedYield'
        | 'estimatedPrice',
      value: string,
    ) => {
      setEditingRevenueRow((current) => {
        if (!current) return current;
        if (field === 'startMonth' || field === 'endMonth') {
          return {...current, [field]: value};
        }
        return {...current, [field]: parseAmount(value)};
      });
    },
    [],
  );

  const saveRevenueModal = useCallback(() => {
    if (!editingRevenueRow) return;

    const hasInvalidNumber =
      editingRevenueRow.numberOfHectares < 0 ||
      editingRevenueRow.estimatedYield < 0 ||
      editingRevenueRow.estimatedPrice < 0;

    if (hasInvalidNumber) {
      setRevenueFormError('Values must be zero or greater.');
      return;
    }

    if (!editingRevenueRow.startMonth || !editingRevenueRow.endMonth) {
      setRevenueFormError('Select both start and end month.');
      return;
    }

    setRevenueRows((current) =>
      current.map((row) =>
        row.planId === editingRevenueRow.planId ? editingRevenueRow : row,
      ),
    );
    closeRevenueModal();
  }, [closeRevenueModal, editingRevenueRow]);

  const openProductionModal = useCallback(
    (row: ProductionRow, trigger: HTMLButtonElement | null) => {
      setEditingProductionRow({...row});
      setProductionFormError('');
      if (trigger) {
        productionModalTriggerRef.current = trigger;
      }
      setIsProductionModalOpen(true);
    },
    [],
  );

  const closeProductionModal = useCallback(() => {
    setIsProductionModalOpen(false);
    setEditingProductionRow(null);
    setProductionFormError('');
  }, []);

  const handleProductionModalFieldChange = useCallback(
    (
      field: 'hectares' | 'seedCost' | 'fertiliserCost' | 'chemicalCost',
      value: string,
    ) => {
      setEditingProductionRow((current) => {
        if (!current) return current;
        return {
          ...current,
          [field]: sanitizeAmountInput(value),
        };
      });
    },
    [],
  );

  const saveProductionModal = useCallback(() => {
    if (!editingProductionRow) return;

    const hectares = parseAmount(editingProductionRow.hectares);
    const seed = parseAmount(editingProductionRow.seedCost);
    const fertiliser = parseAmount(editingProductionRow.fertiliserCost);
    const chemical = parseAmount(editingProductionRow.chemicalCost);

    if (hectares < 0 || seed < 0 || fertiliser < 0 || chemical < 0) {
      setProductionFormError('Values must be zero or greater.');
      return;
    }

    setProductionRows((current) =>
      current.map((row) =>
        row.planId === editingProductionRow.planId ? editingProductionRow : row,
      ),
    );
    closeProductionModal();
  }, [closeProductionModal, editingProductionRow]);

  const openNoteModal = useCallback(
    (
      section: NoteTargetSection,
      id: number,
      label: string,
      currentNote: string,
      trigger: HTMLButtonElement | null,
    ) => {
      setNoteTarget({section, id, label});
      setNoteDraft(currentNote);
      if (trigger) {
        noteModalTriggerRef.current = trigger;
      }
      setIsNoteModalOpen(true);
    },
    [],
  );

  const closeNoteModal = useCallback(() => {
    setIsNoteModalOpen(false);
    setNoteTarget(null);
    setNoteDraft('');
  }, []);

  const saveNoteModal = useCallback(() => {
    if (!noteTarget) return;

    if (noteTarget.section === 'revenue') {
      setRevenueRows((current) =>
        current.map((row) =>
          row.planId === noteTarget.id ? {...row, notes: noteDraft} : row,
        ),
      );
    } else if (noteTarget.section === 'production') {
      setProductionRows((current) =>
        current.map((row) =>
          row.planId === noteTarget.id ? {...row, notes: noteDraft} : row,
        ),
      );
    } else {
      setOperatingRows((current) =>
        current.map((row) =>
          row.localId === noteTarget.id ? {...row, notes: noteDraft} : row,
        ),
      );
    }

    closeNoteModal();
  }, [closeNoteModal, noteDraft, noteTarget]);

  useModalFocusTrap(
    isRevenueModalOpen,
    revenueModalPanelRef as React.RefObject<HTMLDivElement>,
    closeRevenueModal,
  );
  useModalFocusTrap(
    isProductionModalOpen,
    productionModalPanelRef as React.RefObject<HTMLDivElement>,
    closeProductionModal,
  );
  useModalFocusTrap(
    isNoteModalOpen,
    noteModalPanelRef as React.RefObject<HTMLDivElement>,
    closeNoteModal,
  );

  useEffect(() => {
    if (!isRevenueModalOpen && revenueModalTriggerRef.current) {
      const trigger = revenueModalTriggerRef.current;
      window.requestAnimationFrame(() => trigger.focus());
    }
  }, [isRevenueModalOpen]);

  useEffect(() => {
    if (!isProductionModalOpen && productionModalTriggerRef.current) {
      const trigger = productionModalTriggerRef.current;
      window.requestAnimationFrame(() => trigger.focus());
    }
  }, [isProductionModalOpen]);

  useEffect(() => {
    if (!isNoteModalOpen && noteModalTriggerRef.current) {
      const trigger = noteModalTriggerRef.current;
      window.requestAnimationFrame(() => trigger.focus());
    }
  }, [isNoteModalOpen]);

  const updateOperatingRow = useCallback(
    (localId: number, updater: (row: OperatingRow) => OperatingRow) => {
      setOperatingRows((current) =>
        current.map((row) => (row.localId === localId ? updater(row) : row)),
      );
    },
    [],
  );

  const handleOperatingCategoryChange = useCallback(
    (localId: number, expenseId: string) => {
      const selected = catalogue.find((item) => String(item.id) === expenseId);

      updateOperatingRow(localId, (row) => ({
        ...row,
        expenseId,
        categoryName: selected?.name ?? row.categoryName,
      }));
    },
    [catalogue, updateOperatingRow],
  );

  const handleOperatingMonthValueChange = useCallback(
    (localId: number, monthKey: string, value: string) => {
      const numeric = value === '' ? 0 : Number(value);

      updateOperatingRow(localId, (row) => ({
        ...row,
        monthlyValues: {
          ...row.monthlyValues,
          [monthKey]: Number.isFinite(numeric) ? roundMoney(numeric) : 0,
        },
      }));
    },
    [updateOperatingRow],
  );

  const handleAddOperatingRow = useCallback(() => {
    if (catalogue.length === 0) {
      setStatusOnce('No expense catalogue entries available yet.');
      return;
    }

    const defaultExpense = catalogue[0];
    const monthlyValues: MonthlyValueMap = {};
    monthKeys.forEach((key) => {
      monthlyValues[key] = 0;
    });

    setOperatingRows((current) => [
      ...current,
      {
        localId: uniqueLocalId(),
        expenseId: String(defaultExpense.id),
        categoryName: defaultExpense.name || 'Operating expense',
        notes: '',
        frequency: 'custom',
        monthlyValues,
      },
    ]);
  }, [catalogue, monthKeys, setStatusOnce]);

  const handleDeleteOperatingRow = useCallback(
    async (row: OperatingRow) => {
      try {
        if (row.backendId) {
          await apiDeleteFdOperatingCost(row.backendId);
        }

        setOperatingRows((current) =>
          current.filter((item) => item.localId !== row.localId),
        );
        setStatusOnce('Operating cost row removed.');
      } catch (error) {
        console.error('Failed to delete operating cost row', error);
        window.alert('We could not remove that operating cost row right now.');
      }
    },
    [setStatusOnce],
  );

  const downloadTemplate = useCallback(async () => {
    const workbook = new ExcelJS.Workbook();

    const revenueSheet = workbook.addWorksheet(TEMPLATE_SHEET_REVENUE);
    revenueSheet.addRow([
      'Plan ID',
      'Crop',
      'Area (ha)',
      'Yield/ha',
      'Price (R)',
      'Start month',
      'End month',
    ]);
    revenueRows.forEach((row) => {
      revenueSheet.addRow([
        row.planId,
        row.cropName,
        row.numberOfHectares,
        row.estimatedYield,
        row.estimatedPrice,
        row.startMonth,
        row.endMonth,
      ]);
    });

    const productionSheet = workbook.addWorksheet(TEMPLATE_SHEET_PRODUCTION);
    productionSheet.addRow([
      'Plan ID',
      'Crop',
      'Hectares',
      'Seed (R)',
      'Fertiliser (R)',
      'Chemical (R)',
      'Notes',
    ]);
    productionRows.forEach((row) => {
      productionSheet.addRow([
        row.planId,
        row.cropName,
        row.hectares,
        row.seedCost,
        row.fertiliserCost,
        row.chemicalCost,
        row.notes,
      ]);
    });

    const operatingSheet = workbook.addWorksheet(TEMPLATE_SHEET_OPERATING);
    operatingSheet.addRow([
      'Expense ID',
      'Item',
      'Notes',
      ...monthColumns.map((month) => month.label),
    ]);
    operatingRows.forEach((row) => {
      operatingSheet.addRow([
        row.expenseId,
        row.categoryName,
        row.notes,
        ...monthKeys.map((monthKey) =>
          roundMoney(Number(row.monthlyValues[monthKey] ?? 0)),
        ),
      ]);
    });

    const settingsSheet = workbook.addWorksheet(TEMPLATE_SHEET_SETTINGS);
    settingsSheet.addRow(['Setting', 'Value']);
    settingsSheet.addRow([
      'Period start month',
      MONTH_NAMES[periodStartMonthIndex],
    ]);
    settingsSheet.addRow(['Period start year', periodStartYear]);

    const buffer = await workbook.xlsx.writeBuffer();
    const fileBlob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(fileBlob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'cashflow-template.xlsx';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, [
    monthColumns,
    monthKeys,
    operatingRows,
    periodStartMonthIndex,
    periodStartYear,
    productionRows,
    revenueRows,
  ]);

  const openUploadTemplatePicker = useCallback(() => {
    uploadInputRef.current?.click();
  }, []);

  const handleTemplateUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      const file = input.files?.[0];
      if (!file) return;

      const lowerName = file.name.toLowerCase();
      const isWorkbook =
        lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls');

      if (!isWorkbook) {
        window.alert('Please upload an Excel template file (.xlsx or .xls).');
        input.value = '';
        return;
      }

      const approvedByName = new Map(
        catalogue
          .filter((item) => Boolean(item.name?.trim()))
          .map((item) => [item.name!.trim().toLowerCase(), item] as const),
      );
      const approvedById = new Map(
        catalogue.map((item) => [String(item.id), item] as const),
      );

      try {
        const content = await file.arrayBuffer();
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(content);

        let importedRevenueCount = 0;
        let importedProductionCount = 0;
        let importedOperatingCount = 0;

        const nextRevenueRows = [...revenueRows];
        const nextProductionRows = [...productionRows];
        let nextOperatingRows = [...operatingRows];

        const revenueSheet = workbook.getWorksheet(TEMPLATE_SHEET_REVENUE);
        if (revenueSheet) {
          const parsedRows = worksheetToMatrix(revenueSheet);

          if (parsedRows.length > 1) {
            const headerRow = (parsedRows[0] ?? []).map((cell) =>
              normaliseHeader(String(cell ?? '')),
            );

            let planIdIndex = -1;
            let cropIndex = -1;
            let hectaresIndex = -1;
            let yieldIndex = -1;
            let priceIndex = -1;
            let startMonthIndex = -1;
            let endMonthIndex = -1;

            headerRow.forEach((header, index) => {
              if (planIdIndex === -1 && header.includes('plan id')) {
                planIdIndex = index;
              } else if (
                cropIndex === -1 &&
                (header === 'crop' || header.includes('crop'))
              ) {
                cropIndex = index;
              } else if (
                hectaresIndex === -1 &&
                (header.includes('area') || header.includes('hectare'))
              ) {
                hectaresIndex = index;
              } else if (yieldIndex === -1 && header.includes('yield')) {
                yieldIndex = index;
              } else if (priceIndex === -1 && header.includes('price')) {
                priceIndex = index;
              } else if (
                startMonthIndex === -1 &&
                header.includes('start month')
              ) {
                startMonthIndex = index;
              } else if (endMonthIndex === -1 && header.includes('end month')) {
                endMonthIndex = index;
              }
            });

            const rowsByPlanId = new Map<number, number>();
            const rowsByCropName = new Map<string, number>();
            nextRevenueRows.forEach((row, index) => {
              rowsByPlanId.set(row.planId, index);
              rowsByCropName.set(row.cropName.trim().toLowerCase(), index);
            });

            for (
              let rowIndex = 1;
              rowIndex < parsedRows.length;
              rowIndex += 1
            ) {
              const sourceRow = parsedRows[rowIndex] ?? [];
              const hasData = sourceRow.some(
                (cell) => String(cell ?? '').trim().length > 0,
              );
              if (!hasData) continue;

              const planIdRaw =
                planIdIndex >= 0
                  ? String(sourceRow[planIdIndex] ?? '').trim()
                  : '';
              const cropRaw =
                cropIndex >= 0
                  ? String(sourceRow[cropIndex] ?? '')
                      .trim()
                      .toLowerCase()
                  : '';

              const planId = Number(planIdRaw);
              const targetIndex = Number.isFinite(planId)
                ? rowsByPlanId.get(planId)
                : rowsByCropName.get(cropRaw);

              if (targetIndex === undefined) continue;

              const next = {...nextRevenueRows[targetIndex]};

              if (hectaresIndex >= 0) {
                const raw = String(sourceRow[hectaresIndex] ?? '').trim();
                if (raw) {
                  const parsed = parseTemplateNumber(raw);
                  if (parsed !== null) {
                    next.numberOfHectares = parsed;
                  }
                }
              }

              if (yieldIndex >= 0) {
                const raw = String(sourceRow[yieldIndex] ?? '').trim();
                if (raw) {
                  const parsed = parseTemplateNumber(raw);
                  if (parsed !== null) {
                    next.estimatedYield = parsed;
                  }
                }
              }

              if (priceIndex >= 0) {
                const raw = String(sourceRow[priceIndex] ?? '').trim();
                if (raw) {
                  const parsed = parseTemplateNumber(raw);
                  if (parsed !== null) {
                    next.estimatedPrice = parsed;
                  }
                }
              }

              if (startMonthIndex >= 0) {
                const month = resolveMonthName(sourceRow[startMonthIndex]);
                if (month) {
                  next.startMonth = month;
                }
              }

              if (endMonthIndex >= 0) {
                const month = resolveMonthName(sourceRow[endMonthIndex]);
                if (month) {
                  next.endMonth = month;
                }
              }

              nextRevenueRows[targetIndex] = next;
              importedRevenueCount += 1;
            }
          }
        }

        const productionSheet = workbook.getWorksheet(
          TEMPLATE_SHEET_PRODUCTION,
        );
        if (productionSheet) {
          const parsedRows = worksheetToMatrix(productionSheet);

          if (parsedRows.length > 1) {
            const headerRow = (parsedRows[0] ?? []).map((cell) =>
              normaliseHeader(String(cell ?? '')),
            );

            let planIdIndex = -1;
            let cropIndex = -1;
            let hectaresIndex = -1;
            let seedIndex = -1;
            let fertiliserIndex = -1;
            let chemicalIndex = -1;
            let notesIndex = -1;

            headerRow.forEach((header, index) => {
              if (planIdIndex === -1 && header.includes('plan id')) {
                planIdIndex = index;
              } else if (
                cropIndex === -1 &&
                (header === 'crop' || header.includes('crop'))
              ) {
                cropIndex = index;
              } else if (
                hectaresIndex === -1 &&
                (header.includes('hectares') || header.includes('area'))
              ) {
                hectaresIndex = index;
              } else if (seedIndex === -1 && header.includes('seed')) {
                seedIndex = index;
              } else if (
                fertiliserIndex === -1 &&
                (header.includes('fertiliser') || header.includes('fertilizer'))
              ) {
                fertiliserIndex = index;
              } else if (chemicalIndex === -1 && header.includes('chemical')) {
                chemicalIndex = index;
              } else if (notesIndex === -1 && header.includes('note')) {
                notesIndex = index;
              }
            });

            const rowsByPlanId = new Map<number, number>();
            const rowsByCropName = new Map<string, number>();
            nextProductionRows.forEach((row, index) => {
              rowsByPlanId.set(row.planId, index);
              rowsByCropName.set(row.cropName.trim().toLowerCase(), index);
            });

            for (
              let rowIndex = 1;
              rowIndex < parsedRows.length;
              rowIndex += 1
            ) {
              const sourceRow = parsedRows[rowIndex] ?? [];
              const hasData = sourceRow.some(
                (cell) => String(cell ?? '').trim().length > 0,
              );
              if (!hasData) continue;

              const planIdRaw =
                planIdIndex >= 0
                  ? String(sourceRow[planIdIndex] ?? '').trim()
                  : '';
              const cropRaw =
                cropIndex >= 0
                  ? String(sourceRow[cropIndex] ?? '')
                      .trim()
                      .toLowerCase()
                  : '';

              const planId = Number(planIdRaw);
              const targetIndex = Number.isFinite(planId)
                ? rowsByPlanId.get(planId)
                : rowsByCropName.get(cropRaw);

              if (targetIndex === undefined) continue;

              const next = {...nextProductionRows[targetIndex]};
              const applyValue = (
                columnIndex: number,
                assign: (value: string) => void,
              ) => {
                if (columnIndex < 0) return;

                const raw = String(sourceRow[columnIndex] ?? '').trim();
                if (!raw) return;

                const parsed = parseTemplateNumber(raw);
                if (parsed === null) return;
                assign(formatInput(parsed));
              };

              applyValue(hectaresIndex, (value) => {
                next.hectares = value;
              });
              applyValue(seedIndex, (value) => {
                next.seedCost = value;
              });
              applyValue(fertiliserIndex, (value) => {
                next.fertiliserCost = value;
              });
              applyValue(chemicalIndex, (value) => {
                next.chemicalCost = value;
              });

              if (notesIndex >= 0) {
                next.notes = String(sourceRow[notesIndex] ?? '').trim();
              }

              nextProductionRows[targetIndex] = next;
              importedProductionCount += 1;
            }
          }
        }

        const operatingSheet = workbook.getWorksheet(TEMPLATE_SHEET_OPERATING);
        if (operatingSheet) {
          const parsedRows = worksheetToMatrix(operatingSheet);

          if (parsedRows.length > 1) {
            const headerRow = (parsedRows[0] ?? []).map((cell) =>
              String(cell ?? ''),
            );

            let expenseIdIndex = -1;
            let itemIndex = -1;
            let notesIndex = -1;
            const monthColumnIndexes = new Map<string, number>();

            headerRow.forEach((header, index) => {
              const normalised = normaliseHeader(header);
              if (expenseIdIndex === -1 && normalised.includes('expense id')) {
                expenseIdIndex = index;
              } else if (
                itemIndex === -1 &&
                ['item', 'expense', 'expense item', 'category'].includes(
                  normalised,
                )
              ) {
                itemIndex = index;
              } else if (
                notesIndex === -1 &&
                ['notes', 'note'].includes(normalised)
              ) {
                notesIndex = index;
              }

              const monthKey = monthKeyFromTemplateHeader(header);
              if (monthKey) {
                monthColumnIndexes.set(monthKey, index);
              }
            });

            const importedRows: OperatingRow[] = [];

            for (
              let rowIndex = 1;
              rowIndex < parsedRows.length;
              rowIndex += 1
            ) {
              const sourceRow = parsedRows[rowIndex] ?? [];
              const hasData = sourceRow.some(
                (cell) => String(cell ?? '').trim().length > 0,
              );
              if (!hasData) continue;

              const expenseIdRaw =
                expenseIdIndex >= 0
                  ? String(sourceRow[expenseIdIndex] ?? '').trim()
                  : '';
              const itemRaw =
                itemIndex >= 0 ? String(sourceRow[itemIndex] ?? '').trim() : '';
              const notesRaw =
                notesIndex >= 0
                  ? String(sourceRow[notesIndex] ?? '').trim()
                  : '';

              const resolvedItem =
                approvedById.get(expenseIdRaw) ??
                approvedByName.get(itemRaw.toLowerCase()) ??
                approvedById.get(itemRaw);

              if (!resolvedItem) continue;

              const monthlyValues: MonthlyValueMap = {};
              monthKeys.forEach((key) => {
                monthlyValues[key] = 0;
              });

              monthKeys.forEach((monthKey) => {
                const columnIndex = monthColumnIndexes.get(monthKey);
                if (columnIndex === undefined) return;

                const parsedAmount = parseTemplateAmount(
                  sourceRow[columnIndex],
                );
                if (parsedAmount === null || parsedAmount < 0) return;
                monthlyValues[monthKey] = roundMoney(parsedAmount);
              });

              importedRows.push({
                localId: uniqueLocalId(),
                expenseId: String(resolvedItem.id),
                categoryName: resolvedItem.name || 'Operating expense',
                notes: notesRaw,
                frequency: 'custom',
                monthlyValues,
              });
            }

            if (importedRows.length > 0) {
              const merged = [...nextOperatingRows];
              const usedIndices = new Set<number>();

              importedRows.forEach((incoming) => {
                const matchIndex = merged.findIndex(
                  (row, index) =>
                    !usedIndices.has(index) &&
                    String(row.expenseId) === String(incoming.expenseId),
                );

                if (matchIndex !== -1) {
                  merged[matchIndex] = {
                    ...merged[matchIndex],
                    categoryName: incoming.categoryName,
                    notes: incoming.notes,
                    monthlyValues: incoming.monthlyValues,
                  };
                  usedIndices.add(matchIndex);
                } else {
                  merged.push(incoming);
                }
              });

              nextOperatingRows = merged;
              importedOperatingCount = importedRows.length;
            }
          }
        }

        if (
          importedRevenueCount === 0 &&
          importedProductionCount === 0 &&
          importedOperatingCount === 0
        ) {
          throw new Error(
            'No matching template rows were found for projected revenue, production costs or operating costs.',
          );
        }

        setRevenueRows(nextRevenueRows);
        setProductionRows(nextProductionRows);
        setOperatingRows(nextOperatingRows);

        const summary = [
          importedRevenueCount > 0 ? `${importedRevenueCount} revenue` : null,
          importedProductionCount > 0
            ? `${importedProductionCount} production cost`
            : null,
          importedOperatingCount > 0
            ? `${importedOperatingCount} operating cost`
            : null,
        ]
          .filter(Boolean)
          .join(', ');

        setStatusOnce(`Imported ${summary} row(s) from template.`);
      } catch (error: any) {
        console.error('Template import failed', error);
        window.alert(
          error?.message ||
            'We could not import the template. Please check the file format.',
        );
      } finally {
        input.value = '';
      }
    },
    [
      catalogue,
      monthKeys,
      operatingRows,
      productionRows,
      revenueRows,
      setStatusOnce,
    ],
  );

  const revenueTotalsByMonth = useMemo(() => {
    const totals: MonthlyValueMap = {};
    monthKeys.forEach((key) => {
      totals[key] = 0;
    });

    revenueRows.forEach((row) => {
      const monthly = revenueMonthlyValues(row, monthKeys);
      monthKeys.forEach((key) => {
        totals[key] = roundMoney(totals[key] + Number(monthly[key] ?? 0));
      });
    });

    return totals;
  }, [monthKeys, revenueRows]);

  const productionTotalsByMonth = useMemo(() => {
    const totals: MonthlyValueMap = {};
    monthKeys.forEach((key) => {
      totals[key] = 0;
    });

    productionRows.forEach((row) => {
      const monthly = productionMonthlyValues(row, monthKeys);
      monthKeys.forEach((key) => {
        totals[key] = roundMoney(totals[key] + Number(monthly[key] ?? 0));
      });
    });

    return totals;
  }, [monthKeys, productionRows]);

  const operatingTotalsByMonth = useMemo(() => {
    const totals: MonthlyValueMap = {};
    monthKeys.forEach((key) => {
      totals[key] = 0;
    });

    operatingRows.forEach((row) => {
      monthKeys.forEach((key) => {
        totals[key] = roundMoney(
          totals[key] + roundMoney(Number(row.monthlyValues[key] ?? 0)),
        );
      });
    });

    return totals;
  }, [monthKeys, operatingRows]);

  const revenueGrandTotal = useMemo(
    () =>
      roundMoney(revenueRows.reduce((sum, row) => sum + revenueTotal(row), 0)),
    [revenueRows],
  );

  const productionGrandTotal = useMemo(
    () =>
      roundMoney(
        productionRows.reduce((sum, row) => sum + productionTotal(row), 0),
      ),
    [productionRows],
  );

  const operatingGrandTotal = useMemo(
    () =>
      roundMoney(
        operatingRows.reduce(
          (sum, row) => sum + totalForOperatingRow(row, monthKeys),
          0,
        ),
      ),
    [monthKeys, operatingRows],
  );

  const netProfitByMonth = useMemo(() => {
    const totals: MonthlyValueMap = {};

    monthKeys.forEach((key) => {
      totals[key] = roundMoney(
        Number(revenueTotalsByMonth[key] ?? 0) -
          Number(productionTotalsByMonth[key] ?? 0) -
          Number(operatingTotalsByMonth[key] ?? 0),
      );
    });

    return totals;
  }, [
    monthKeys,
    operatingTotalsByMonth,
    productionTotalsByMonth,
    revenueTotalsByMonth,
  ]);

  const runningBalanceByMonth = useMemo(() => {
    const balances: MonthlyValueMap = {};
    let runningTotal = 0;

    monthKeys.forEach((key) => {
      runningTotal = roundMoney(
        runningTotal + Number(netProfitByMonth[key] ?? 0),
      );
      balances[key] = runningTotal;
    });

    return balances;
  }, [monthKeys, netProfitByMonth]);

  const netProfitTotal = useMemo(
    () =>
      roundMoney(
        revenueGrandTotal - productionGrandTotal - operatingGrandTotal,
      ),
    [operatingGrandTotal, productionGrandTotal, revenueGrandTotal],
  );

  const runningBalanceTotal = useMemo(() => {
    const lastMonthKey = monthKeys[monthKeys.length - 1];
    return roundMoney(Number(runningBalanceByMonth[lastMonthKey] ?? 0));
  }, [monthKeys, runningBalanceByMonth]);

  const noteModalFrequency = useMemo(() => {
    if (!noteTarget) return null;
    if (noteTarget.section === 'production') return 'Auto';
    if (noteTarget.section !== 'operating') return null;

    const row = operatingRows.find((item) => item.localId === noteTarget.id);
    if (!row) return null;

    return buildOperatingPayload(row, monthKeys).frequency;
  }, [monthKeys, noteTarget, operatingRows]);

  const columnsAfterItem = monthColumns.length + 2;

  return (
    <div className="financials-cashflow">
      <div className="sr-only" role="status" aria-live="polite">
        {statusMessage}
      </div>

      <div className="financials-cashflow__controls">
        <div className="financials-cashflow__period">
          <label htmlFor="cashflow-period-start-month">Period start</label>
          <div className="financials-cashflow__period-fields">
            <select
              id="cashflow-period-start-month"
              value={periodStartMonthIndex}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value) || value < 0 || value > 11) return;
                setPeriodStartMonthIndex(Math.trunc(value));
              }}
              disabled={isSaving}
            >
              {MONTH_NAMES.map((month, index) => (
                <option key={month} value={index}>
                  {month}
                </option>
              ))}
            </select>
            <select
              aria-label="Period start year"
              value={periodStartYear}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) return;
                setPeriodStartYear(normalisePeriodYear(value));
              }}
              disabled={isSaving}
            >
              {periodYearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <p className="financials-cashflow__period-meta">
            Table month columns run from {periodLabel} for {PERIOD_MONTHS}{' '}
            months.
          </p>
        </div>

        <div className="financials-template-actions">
          <button
            type="button"
            className="btn btn-tertiary"
            onClick={() => {
              void downloadTemplate();
            }}
            disabled={isLoading || isSaving}
          >
            <FontAwesomeIcon icon={iconDownload} aria-hidden="true" />
            <span>Download template</span>
          </button>
          <button
            type="button"
            className="btn btn-tertiary"
            onClick={openUploadTemplatePicker}
            disabled={isLoading || isSaving}
          >
            <FontAwesomeIcon icon={iconUpload} aria-hidden="true" />
            <span>Upload template</span>
          </button>
          <button
            type="button"
            className="btn btn-tertiary"
            onClick={handleAddOperatingRow}
            disabled={catalogue.length === 0 || isLoading || isSaving}
          >
            <FontAwesomeIcon icon={iconPlus} aria-hidden="true" />
            <span>Add operating row</span>
          </button>
          <input
            ref={uploadInputRef}
            className="sr-only"
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(event) => {
              void handleTemplateUpload(event);
            }}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="financials-cashflow__loading" role="status">
          <FontAwesomeIcon icon={iconSpinner} spin />
          <span>Loading cashflow data…</span>
        </div>
      ) : null}

      {!isLoading && revenueRows.length === 0 ? (
        <div className="application-card">
          <div className="application-empty-state">
            <h3>No production plans found</h3>
            <p>
              Add crops to your production plan to capture projected revenue and
              production costs.
            </p>
          </div>
        </div>
      ) : null}

      <div className="financials-cashflow__table-wrap" aria-live="polite">
        <table className="financials-cashflow-table">
          <thead>
            <tr>
              <th scope="col">Item</th>
              {monthColumns.map((month) => (
                <th key={`cashflow-month-${month.key}`} scope="col">
                  {month.label}
                </th>
              ))}
              <th scope="col">Total (R)</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr className="financials-cashflow-table__section-row">
              <th
                scope="row"
                className="financials-cashflow-table__section-label"
              >
                Projected revenue
              </th>
              <td colSpan={columnsAfterItem} />
            </tr>
            {revenueRows.map((row) => {
              const monthly = revenueMonthlyValues(row, monthKeys);
              const total = revenueTotal(row);

              return (
                <tr key={`revenue-${row.planId}`}>
                  <th
                    scope="row"
                    className="financials-cashflow-table__item-cell"
                  >
                    <div className="financials-cashflow-table__item-content">
                      <span>{row.cropName}</span>
                      <button
                        type="button"
                        className="icon-button financials-cashflow-table__edit-btn"
                        onClick={(event) =>
                          openRevenueModal(row, event.currentTarget)
                        }
                        disabled={isSaving}
                        aria-label={`Edit projected revenue for ${row.cropName}`}
                      >
                        <FontAwesomeIcon icon={iconPen} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="icon-button financials-cashflow-table__note-btn"
                        onClick={(event) =>
                          openNoteModal(
                            'revenue',
                            row.planId,
                            row.cropName,
                            row.notes,
                            event.currentTarget,
                          )
                        }
                        disabled={isSaving}
                        aria-label={`Edit note for ${row.cropName}`}
                      >
                        <FontAwesomeIcon
                          icon={iconNoteSticky}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </th>
                  {monthKeys.map((monthKey) => (
                    <td key={`revenue-${row.planId}-${monthKey}`}>
                      {toCurrency(Number(monthly[monthKey] ?? 0))}
                    </td>
                  ))}
                  <td className="financials-cashflow-table__total-cell">
                    {toCurrency(total)}
                  </td>
                  <td className="financials-cashflow-table__muted">-</td>
                </tr>
              );
            })}
            <tr className="financials-cashflow-table__totals-row">
              <th
                scope="row"
                className="financials-cashflow-table__totals-label"
              >
                Projected revenue total
              </th>
              {monthKeys.map((monthKey) => (
                <td key={`revenue-total-${monthKey}`}>
                  {toCurrency(Number(revenueTotalsByMonth[monthKey] ?? 0))}
                </td>
              ))}
              <td>{toCurrency(revenueGrandTotal)}</td>
              <td />
            </tr>

            <tr className="financials-cashflow-table__section-row">
              <th
                scope="row"
                className="financials-cashflow-table__section-label"
              >
                Production costs
              </th>
              <td colSpan={columnsAfterItem} />
            </tr>
            {productionRows.map((row) => {
              const monthly = productionMonthlyValues(row, monthKeys);
              const total = productionTotal(row);

              return (
                <tr key={`production-${row.planId}`}>
                  <th
                    scope="row"
                    className="financials-cashflow-table__item-cell"
                  >
                    <div className="financials-cashflow-table__item-content">
                      <span>{row.cropName}</span>
                      <button
                        type="button"
                        className="icon-button financials-cashflow-table__edit-btn"
                        onClick={(event) =>
                          openProductionModal(row, event.currentTarget)
                        }
                        disabled={isSaving}
                        aria-label={`Edit production costs for ${row.cropName}`}
                      >
                        <FontAwesomeIcon icon={iconPen} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="icon-button financials-cashflow-table__note-btn"
                        onClick={(event) =>
                          openNoteModal(
                            'production',
                            row.planId,
                            row.cropName,
                            row.notes,
                            event.currentTarget,
                          )
                        }
                        disabled={isSaving}
                        aria-label={`Edit note for ${row.cropName}`}
                      >
                        <FontAwesomeIcon
                          icon={iconNoteSticky}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </th>
                  {monthKeys.map((monthKey) => (
                    <td key={`production-${row.planId}-${monthKey}`}>
                      {toCurrency(Number(monthly[monthKey] ?? 0))}
                    </td>
                  ))}
                  <td className="financials-cashflow-table__total-cell">
                    {toCurrency(total)}
                  </td>
                  <td className="financials-cashflow-table__muted">-</td>
                </tr>
              );
            })}
            <tr className="financials-cashflow-table__totals-row">
              <th
                scope="row"
                className="financials-cashflow-table__totals-label"
              >
                Production costs total
              </th>
              {monthKeys.map((monthKey) => (
                <td key={`production-total-${monthKey}`}>
                  {toCurrency(Number(productionTotalsByMonth[monthKey] ?? 0))}
                </td>
              ))}
              <td>{toCurrency(productionGrandTotal)}</td>
              <td />
            </tr>

            <tr className="financials-cashflow-table__section-row">
              <th
                scope="row"
                className="financials-cashflow-table__section-label"
              >
                Operating costs
              </th>
              <td colSpan={columnsAfterItem} />
            </tr>
            {operatingRows.map((row) => {
              const payload = buildOperatingPayload(row, monthKeys);

              return (
                <tr key={`operating-${row.localId}`}>
                  <td className="financials-cashflow-table__item-cell">
                    <select
                      className="financials-cashflow-table__select"
                      value={row.expenseId}
                      onChange={(event) =>
                        handleOperatingCategoryChange(
                          row.localId,
                          event.target.value,
                        )
                      }
                      disabled={isSaving}
                    >
                      {!catalogue.some(
                        (item) => String(item.id) === String(row.expenseId),
                      ) ? (
                        <option value={row.expenseId}>
                          {row.categoryName}
                        </option>
                      ) : null}
                      {catalogue.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  {monthKeys.map((monthKey) => {
                    const value = roundMoney(
                      Number(row.monthlyValues[monthKey] ?? 0),
                    );

                    return (
                      <td key={`operating-${row.localId}-${monthKey}`}>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="financials-cashflow-table__month-input"
                          value={value > 0 ? value : ''}
                          onChange={(event) =>
                            handleOperatingMonthValueChange(
                              row.localId,
                              monthKey,
                              event.target.value,
                            )
                          }
                          disabled={isSaving}
                          placeholder="0"
                        />
                      </td>
                    );
                  })}
                  <td className="financials-cashflow-table__total-cell">
                    {toCurrency(payload.total)}
                  </td>
                  <td>
                    <div className="financials-cashflow-table__action-group">
                      <button
                        type="button"
                        className="icon-button financials-cashflow-table__note-btn"
                        onClick={(event) =>
                          openNoteModal(
                            'operating',
                            row.localId,
                            row.categoryName,
                            row.notes,
                            event.currentTarget,
                          )
                        }
                        disabled={isSaving}
                        aria-label={`Edit note for ${row.categoryName}`}
                      >
                        <FontAwesomeIcon
                          icon={iconNoteSticky}
                          aria-hidden="true"
                        />
                      </button>
                      <button
                        type="button"
                        className="btn btn-tertiary is-danger financials-cashflow-table__delete-btn"
                        onClick={() => {
                          void handleDeleteOperatingRow(row);
                        }}
                        disabled={isSaving}
                        aria-label={`Delete ${row.categoryName}`}
                      >
                        <FontAwesomeIcon icon={iconTrash} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            <tr className="financials-cashflow-table__totals-row">
              <th
                scope="row"
                className="financials-cashflow-table__totals-label"
              >
                Operating costs total
              </th>
              {monthKeys.map((monthKey) => (
                <td key={`operating-total-${monthKey}`}>
                  {toCurrency(Number(operatingTotalsByMonth[monthKey] ?? 0))}
                </td>
              ))}
              <td>{toCurrency(operatingGrandTotal)}</td>
              <td />
            </tr>

            <tr className="financials-cashflow-table__totals-row">
              <th
                scope="row"
                className="financials-cashflow-table__totals-label"
              >
                Profit
              </th>
              {monthKeys.map((monthKey) => (
                <td key={`net-profit-${monthKey}`}>
                  {toCurrency(Number(netProfitByMonth[monthKey] ?? 0))}
                </td>
              ))}
              <td>{toCurrency(netProfitTotal)}</td>
              <td />
            </tr>

            <tr className="financials-cashflow-table__totals-row">
              <th
                scope="row"
                className="financials-cashflow-table__totals-label"
              >
                Running balance
              </th>
              {monthKeys.map((monthKey) => (
                <td key={`running-balance-${monthKey}`}>
                  {toCurrency(Number(runningBalanceByMonth[monthKey] ?? 0))}
                </td>
              ))}
              <td>{toCurrency(runningBalanceTotal)}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>

      <div
        className="financials-cashflow__ratios"
        style={{
          marginTop: '2rem',
          padding: '1rem',
          background: '#f9fafb',
          borderRadius: '8px',
        }}
      >
        <h3
          style={{
            marginBottom: '1rem',
            fontSize: '1.2rem',
            fontWeight: 'bold',
          }}
        >
          Financial Ratios
        </h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
          }}
        >
          <div
            className="ratio-card"
            style={{
              padding: '1rem',
              background: '#fff',
              borderRadius: '4px',
              border: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                fontSize: '0.875rem',
                color: '#6b7280',
                marginBottom: '0.25rem',
              }}
            >
              Loan-to-Value (LTV)
            </div>
            <div style={{fontSize: '1.25rem', fontWeight: '600'}}>
              {financialRatios?.ltv != null
                ? `${financialRatios.ltv.toFixed(2)}%`
                : 'N/A'}
            </div>
          </div>
          <div
            className="ratio-card"
            style={{
              padding: '1rem',
              background: '#fff',
              borderRadius: '4px',
              border: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                fontSize: '0.875rem',
                color: '#6b7280',
                marginBottom: '0.25rem',
              }}
            >
              DSCR
            </div>
            <div style={{fontSize: '1.25rem', fontWeight: '600'}}>
              {financialRatios?.dscr != null
                ? financialRatios.dscr.toFixed(2)
                : 'N/A'}
            </div>
          </div>
          <div
            className="ratio-card"
            style={{
              padding: '1rem',
              background: '#fff',
              borderRadius: '4px',
              border: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                fontSize: '0.875rem',
                color: '#6b7280',
                marginBottom: '0.25rem',
              }}
            >
              Gross Margin
            </div>
            <div style={{fontSize: '1.25rem', fontWeight: '600'}}>
              {financialRatios?.grossMargin != null
                ? `${financialRatios.grossMargin.toFixed(2)}%`
                : 'N/A'}
            </div>
          </div>
          <div
            className="ratio-card"
            style={{
              padding: '1rem',
              background: '#fff',
              borderRadius: '4px',
              border: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                fontSize: '0.875rem',
                color: '#6b7280',
                marginBottom: '0.25rem',
              }}
            >
              Net Profit Margin
            </div>
            <div style={{fontSize: '1.25rem', fontWeight: '600'}}>
              {financialRatios?.netProfitMargin != null
                ? `${financialRatios.netProfitMargin.toFixed(2)}%`
                : 'N/A'}
            </div>
          </div>
          <div
            className="ratio-card"
            style={{
              padding: '1rem',
              background: '#fff',
              borderRadius: '4px',
              border: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                fontSize: '0.875rem',
                color: '#6b7280',
                marginBottom: '0.25rem',
              }}
            >
              Debt to Equity
            </div>
            <div style={{fontSize: '1.25rem', fontWeight: '600'}}>
              {financialRatios?.debtToEquity != null
                ? financialRatios.debtToEquity.toFixed(2)
                : 'N/A'}
            </div>
          </div>
          <div
            className="ratio-card"
            style={{
              padding: '1rem',
              background: '#fff',
              borderRadius: '4px',
              border: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                fontSize: '0.875rem',
                color: '#6b7280',
                marginBottom: '0.25rem',
              }}
            >
              Interest Cover
            </div>
            <div style={{fontSize: '1.25rem', fontWeight: '600'}}>
              {financialRatios?.interestCover != null
                ? financialRatios.interestCover.toFixed(2)
                : 'N/A'}
            </div>
          </div>
        </div>
      </div>

      {isRevenueModalOpen && editingRevenueRow ? (
        <div
          className="financials-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="revenue-modal-title"
        >
          <div
            className="financials-modal__backdrop"
            aria-hidden="true"
            onClick={closeRevenueModal}
          />
          <div className="financials-modal__panel" ref={revenueModalPanelRef}>
            <header className="financials-modal__header">
              <h2 id="revenue-modal-title">
                Edit projected revenue: {editingRevenueRow.cropName}
              </h2>
              <button
                type="button"
                className="icon-button"
                onClick={closeRevenueModal}
                aria-label="Close modal"
              >
                <FontAwesomeIcon icon={iconTimes} />
              </button>
            </header>
            <form
              className="financials-modal__form"
              onSubmit={(event) => {
                event.preventDefault();
                saveRevenueModal();
              }}
            >
              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="revenue-start-month">Start month</label>
                  <select
                    id="revenue-start-month"
                    value={editingRevenueRow.startMonth}
                    onChange={(event) =>
                      handleRevenueModalFieldChange(
                        'startMonth',
                        event.target.value,
                      )
                    }
                  >
                    {MONTH_NAMES.map((month) => (
                      <option key={month} value={month}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label htmlFor="revenue-end-month">End month</label>
                  <select
                    id="revenue-end-month"
                    value={editingRevenueRow.endMonth}
                    onChange={(event) =>
                      handleRevenueModalFieldChange(
                        'endMonth',
                        event.target.value,
                      )
                    }
                  >
                    {MONTH_NAMES.map((month) => (
                      <option key={month} value={month}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="revenue-hectares">Area (ha)</label>
                  <input
                    id="revenue-hectares"
                    type="number"
                    min="0"
                    step="0.01"
                    value={editingRevenueRow.numberOfHectares}
                    onChange={(event) =>
                      handleRevenueModalFieldChange(
                        'numberOfHectares',
                        event.target.value,
                      )
                    }
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="revenue-yield">Yield/ha</label>
                  <input
                    id="revenue-yield"
                    type="number"
                    min="0"
                    step="0.01"
                    value={editingRevenueRow.estimatedYield}
                    onChange={(event) =>
                      handleRevenueModalFieldChange(
                        'estimatedYield',
                        event.target.value,
                      )
                    }
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="revenue-price">Price (R)</label>
                  <input
                    id="revenue-price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={editingRevenueRow.estimatedPrice}
                    onChange={(event) =>
                      handleRevenueModalFieldChange(
                        'estimatedPrice',
                        event.target.value,
                      )
                    }
                  />
                </div>
              </div>

              {revenueFormError ? (
                <p className="form-error" role="alert">
                  {revenueFormError}
                </p>
              ) : null}

              <footer className="financials-modal__footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeRevenueModal}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      {isProductionModalOpen && editingProductionRow ? (
        <div
          className="financials-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="production-modal-title"
        >
          <div
            className="financials-modal__backdrop"
            aria-hidden="true"
            onClick={closeProductionModal}
          />
          <div
            className="financials-modal__panel"
            ref={productionModalPanelRef}
          >
            <header className="financials-modal__header">
              <h2 id="production-modal-title">
                Edit production costs: {editingProductionRow.cropName}
              </h2>
              <button
                type="button"
                className="icon-button"
                onClick={closeProductionModal}
                aria-label="Close modal"
              >
                <FontAwesomeIcon icon={iconTimes} />
              </button>
            </header>
            <form
              className="financials-modal__form"
              onSubmit={(event) => {
                event.preventDefault();
                saveProductionModal();
              }}
            >
              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="production-hectares">Hectares</label>
                  <input
                    id="production-hectares"
                    type="text"
                    inputMode="decimal"
                    value={editingProductionRow.hectares}
                    onChange={(event) =>
                      handleProductionModalFieldChange(
                        'hectares',
                        event.target.value,
                      )
                    }
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="production-frequency">Frequency</label>
                  <input
                    id="production-frequency"
                    type="text"
                    value="Auto"
                    disabled
                    readOnly
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="production-seed-cost">Seed (R)</label>
                  <input
                    id="production-seed-cost"
                    type="text"
                    inputMode="decimal"
                    value={editingProductionRow.seedCost}
                    onChange={(event) =>
                      handleProductionModalFieldChange(
                        'seedCost',
                        event.target.value,
                      )
                    }
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="production-fertiliser-cost">
                    Fertiliser (R)
                  </label>
                  <input
                    id="production-fertiliser-cost"
                    type="text"
                    inputMode="decimal"
                    value={editingProductionRow.fertiliserCost}
                    onChange={(event) =>
                      handleProductionModalFieldChange(
                        'fertiliserCost',
                        event.target.value,
                      )
                    }
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="production-chemical-cost">Chemical (R)</label>
                  <input
                    id="production-chemical-cost"
                    type="text"
                    inputMode="decimal"
                    value={editingProductionRow.chemicalCost}
                    onChange={(event) =>
                      handleProductionModalFieldChange(
                        'chemicalCost',
                        event.target.value,
                      )
                    }
                  />
                </div>
              </div>

              {productionFormError ? (
                <p className="form-error" role="alert">
                  {productionFormError}
                </p>
              ) : null}

              <footer className="financials-modal__footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeProductionModal}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      {isNoteModalOpen && noteTarget ? (
        <div
          className="financials-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="note-modal-title"
        >
          <div
            className="financials-modal__backdrop"
            aria-hidden="true"
            onClick={closeNoteModal}
          />
          <div className="financials-modal__panel" ref={noteModalPanelRef}>
            <header className="financials-modal__header">
              <h2 id="note-modal-title">Note: {noteTarget.label}</h2>
              <button
                type="button"
                className="icon-button"
                onClick={closeNoteModal}
                aria-label="Close modal"
              >
                <FontAwesomeIcon icon={iconTimes} />
              </button>
            </header>
            <form
              className="financials-modal__form"
              onSubmit={(event) => {
                event.preventDefault();
                saveNoteModal();
              }}
            >
              {noteModalFrequency ? (
                <div className="form-field">
                  <label htmlFor="cashflow-note-frequency">Frequency</label>
                  <input
                    id="cashflow-note-frequency"
                    type="text"
                    value={noteModalFrequency}
                    disabled
                    readOnly
                  />
                </div>
              ) : null}
              <div className="form-field">
                <label htmlFor="cashflow-note-text">Notes</label>
                <textarea
                  id="cashflow-note-text"
                  rows={5}
                  value={noteDraft}
                  onChange={(event) => setNoteDraft(event.target.value)}
                  placeholder="Add a note..."
                />
              </div>
              <footer className="financials-modal__footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeNoteModal}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save note
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ApplicationFinancialsCashflowStep;
